import { useState } from "react";
import { cn, formatWhatsAppMarkdown } from "@/lib/utils";

const DEFAULT_MAX_LINES = 5;
const DEFAULT_CHAR_THRESHOLD = 220;
const DEFAULT_LINE_BREAK_THRESHOLD = 4;

const LINE_CLAMP_CLASS: Record<number, string> = {
  1: "line-clamp-1",
  2: "line-clamp-2",
  3: "line-clamp-3",
  4: "line-clamp-4",
  5: "line-clamp-5",
  6: "line-clamp-6",
};

export function shouldCollapseMessageText(
  text: string,
  charThreshold = DEFAULT_CHAR_THRESHOLD,
  lineBreakThreshold = DEFAULT_LINE_BREAK_THRESHOLD,
): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;
  return (
    trimmed.length > charThreshold ||
    (trimmed.match(/\n/g)?.length ?? 0) >= lineBreakThreshold
  );
}

type ExpandableMessageTextProps = {
  text: string;
  className?: string;
  maxLines?: number;
  charThreshold?: number;
  lineBreakThreshold?: number;
};

export function ExpandableMessageText({
  text,
  className,
  maxLines = DEFAULT_MAX_LINES,
  charThreshold = DEFAULT_CHAR_THRESHOLD,
  lineBreakThreshold = DEFAULT_LINE_BREAK_THRESHOLD,
}: ExpandableMessageTextProps) {
  const [expanded, setExpanded] = useState(false);
  const collapsible = shouldCollapseMessageText(text, charThreshold, lineBreakThreshold);
  const lineClampClass = LINE_CLAMP_CLASS[maxLines] ?? "line-clamp-5";

  return (
    <div className={cn("min-w-0", className)}>
      <p
        className={cn(
          "whitespace-pre-wrap",
          collapsible && !expanded && lineClampClass,
        )}
        dangerouslySetInnerHTML={{ __html: formatWhatsAppMarkdown(text) }}
      />
      {collapsible && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-1 text-xs font-medium opacity-80 hover:opacity-100 underline-offset-2 hover:underline"
        >
          {expanded ? "Ver menos" : "Ver mais"}
        </button>
      )}
    </div>
  );
}
